/**
 * Cross-window protocol between the admin Content Editor and the
 * public-site iframe(s) it previews. Lets the editor invalidate only the
 * relevant React-Query keys inside an iframe instead of bumping the iframe
 * src (which would trigger a full document reload).
 *
 * Flow:
 *  1. Editor calls postPreviewInvalidate(iframe.contentWindow, payload).
 *  2. PreviewInvalidationListener in the iframe receives it, invalidates
 *     the matching queries, optionally switches the i18n language, then
 *     posts an ack back to the editor window.
 *  3. Editor uses the ack to flip the sync badge to "in sync" without a
 *     full iframe reload. If no ack arrives within ACK_TIMEOUT_MS the
 *     caller can fall back to the existing hard reload path.
 */

export const PREVIEW_INVALIDATE_TYPE = "lovable:admin:preview-invalidate" as const;
export const PREVIEW_ACK_TYPE = "lovable:admin:preview-ack" as const;

export type QueryKey = ReadonlyArray<unknown>;

export interface PreviewInvalidateMessage {
  type: typeof PREVIEW_INVALIDATE_TYPE;
  /** Correlation id — matches the ack so callers can resolve specific requests. */
  id: string;
  /** Query keys to invalidate inside the iframe's QueryClient. */
  queryKeys: QueryKey[];
  /** Optional: switch i18n language inside the iframe before refetching. */
  lang?: "tr" | "en";
}

export interface PreviewAckMessage {
  type: typeof PREVIEW_ACK_TYPE;
  id: string;
  ok: boolean;
  error?: string;
}

/** How long the editor waits for an ack before falling back to a hard reload. */
export const PREVIEW_ACK_TIMEOUT_MS = 2500;

export const isPreviewInvalidateMessage = (
  data: unknown,
): data is PreviewInvalidateMessage =>
  !!data &&
  typeof data === "object" &&
  (data as { type?: unknown }).type === PREVIEW_INVALIDATE_TYPE;

export const isPreviewAckMessage = (
  data: unknown,
): data is PreviewAckMessage =>
  !!data &&
  typeof data === "object" &&
  (data as { type?: unknown }).type === PREVIEW_ACK_TYPE;

/**
 * Post an invalidate request from the editor to a previewed iframe.
 * No-op when targetWindow is null (iframe not mounted yet).
 */
export const postPreviewInvalidate = (
  targetWindow: Window | null,
  payload: Omit<PreviewInvalidateMessage, "type">,
): void => {
  if (!targetWindow) return;
  const msg: PreviewInvalidateMessage = { type: PREVIEW_INVALIDATE_TYPE, ...payload };
  try {
    // Same-origin (admin + preview share host) — "*" is acceptable and avoids
    // brittle origin matching across preview/published subdomains.
    targetWindow.postMessage(msg, "*");
  } catch {
    /* iframe may be navigating; caller handles via timeout */
  }
};
